/** EmitIQ's draft of why the total moved, and the Approver's saved version of it.
 *
 * Its own module rather than two more hooks in `queries.ts`: the draft is **EmitIQ's analysis and not
 * a disclosure**, and keeping it apart from the report's hooks means a component cannot hand it to the
 * report as though the regulation asked for it. The words an Approver saves are stored as a
 * disclosure statement version; the draft itself is never stored.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import type { Scope2Method } from "./queries";
import { unwrap } from "./queries";
import type { components } from "./schema";

export type VarianceNarrative = components["schemas"]["VarianceNarrativeOut"];
export type VarianceDraft = components["schemas"]["VarianceDraftOut"];
export type VarianceFigure = components["schemas"]["VarianceFigureOut"];
export type VarianceSaved = components["schemas"]["VarianceSavedOut"];

const key = (obligationId: string | undefined, method: Scope2Method) =>
  ["report", "variance-narrative", obligationId ?? "", method] as const;

export function useVarianceNarrative(obligationId: string | undefined, method: Scope2Method) {
  return useQuery({
    queryKey: key(obligationId, method),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/obligations/{obligation_id}/variance-narrative", {
          params: {
            path: { obligation_id: obligationId as string },
            query: { scope2_method: method },
          },
        }),
      ),
    enabled: Boolean(obligationId),
  });
}

/** Save the person's text. `expectedVersion` is `saved.version`, or `0` when nothing is saved; a stale
 * one is refused with 409 `stale_version` and the typed text is kept by the caller. */
export function useSaveVarianceNarrative(obligationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { text: string; expectedVersion: number }) =>
      unwrap(
        await api.PUT("/api/v1/reports/obligations/{obligation_id}/variance-narrative", {
          params: { path: { obligation_id: obligationId as string } },
          body: { text: input.text, expected_version: input.expectedVersion },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["report", "variance-narrative", obligationId ?? ""] }),
  });
}
